import { contentTopicRequest } from '../../questionBank/qbankNavigation';
import { EXPLORE_ROUTES } from "../exploreRoutes";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useQueryClient } from '@tanstack/react-query';
import { fetchPodcastDetail } from '../../services/podcastDetailService';
import { podcastDetailFlow, podcastNeedsPurchase, podcastRouteFlow, promptPodcastPurchase } from './podcastAccess';
import { useLocation, useNavigate } from "react-router-dom";
import HomeDashboardLayout from "../HomeDashboardLayout";
import PodcastListCard from "./PodcastListCard";
import QBankFilter from "../../questionBank/QBankFilter";
import "../../dashboard/Dashboard.css";
import "../HomeDashboard.css";
import "../questionBank/QuestionBankList.css";
import HeaderProfileSection from "../HeaderProfileSection";
import { useSidebarData } from "../../hooks/useSidebarData";
import { usePodcastList } from "../../hooks/usePodcastList";
import { usePodcastListActions } from "../../hooks/usePodcastListActions";
import PodcastViewModal from "./PodcastViewModal";
import {
  resolveQuestionBankStateId,
  resolveQuestionBankStateName,
} from "../../questionBank/stateUtils";
import { CONTENT_ROUTES } from "../contentRoutes";
import {
  buildQuestionBankFlowState,
  buildQuestionBankSubjectsState,
  persistQuestionBankFlowState,
} from "../../questionBank/qbankFlowState";

const PODCAST_CATEGORY_ID = 12;
function visiblePages(currentPage, totalPages) {
  const count = Math.min(5, totalPages);
  const startPage =
    totalPages <= count
      ? 1
      : Math.max(
          1,
          Math.min(totalPages - count + 1, currentPage - Math.floor(count / 2)),
        );

  return Array.from({ length: count }, (_, index) => startPage + index);
}

function mapFiltersToPodcastParams(filters = {}) {
  const mapping = {
    languages: "languages",
    tags: "audio_podcast_tags",
    free_paid_filter: "free_paid",
    personal_notes: "personal_notes",
    read_unread: "read_unread",
    bookmark_status: "bookmark_status",
    report_status: "report_status",
  };

  return Object.entries(filters).reduce((params, [key, value]) => {
    if (value !== "" && value !== undefined && value !== null) {
      params[mapping[key] || key] = value;
    }
    return params;
  }, {});
}

const PodcastList = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const client = useQueryClient();
  const purchaseBusy = useRef(false);
  const { data: sidebarData = null } = useSidebarData();
  const flowState = useMemo(
    () => podcastRouteFlow(location.state),
    [location.state],
  );
  const listState = flowState?.listQueryState || {};
  const [currentPage, setCurrentPage] = useState(
    Number(listState.currentPage || 1),
  );
  const [appliedFilters, setAppliedFilters] = useState(
    listState.appliedFilters || {},
  );
  const [searchTerm, setSearchTerm] = useState(
    String(listState.searchTerm || ""),
  );
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(
    String(listState.searchTerm || ""),
  );
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [viewPodcast, setViewPodcast] = useState(null);
  const stateId = useMemo(
    () => resolveQuestionBankStateId({ locationState: flowState, sidebarData }),
    [flowState, sidebarData],
  );
  const subjectId = Number(
    flowState?.selectedSubject?.id || flowState?.subject_id || 0,
  );
  const selectedTopic = flowState.selectedSubtopic || flowState.selectedTopic;
  const filterQueryParams = useMemo(
    () => ({ states_id: stateId, category_id: PODCAST_CATEGORY_ID }),
    [stateId],
  );
  const queryParams = useMemo(
    () => ({
      states_id: stateId,
      category_id: PODCAST_CATEGORY_ID,
      subject_id: subjectId,
      ...contentTopicRequest(flowState),
      ...(flowState.languages ? { languages: flowState.languages } : {}),
      ...(['F', 'P'].includes(flowState.free_paid || flowState.freeStatus) ? { free_paid: flowState.free_paid || flowState.freeStatus } : {}),
      page: currentPage,
      ...(debouncedSearchTerm.trim()
        ? { search_term: debouncedSearchTerm.trim() }
        : {}),
      ...mapFiltersToPodcastParams(appliedFilters),
    }),
    [
      appliedFilters,
      currentPage,
      debouncedSearchTerm,
      flowState,
      stateId,
      subjectId,
    ],
  );
  const { data, isLoading, isFetching, isError, error, refetch, isPlaceholderData } = usePodcastList(
    queryParams,
    { enabled: Boolean(stateId && subjectId) },
  );
  const { toggleBookmark, toggleLike } = usePodcastListActions(queryParams);
  const [loadedPodcasts, setLoadedPodcasts] = useState([]);
  const loadedPages = useRef(new Set());
  const pending = isLoading || isFetching;
  const listScope = JSON.stringify([stateId, subjectId, contentTopicRequest(flowState), flowState.free_paid, flowState.freeStatus, flowState.languages]);
  const screenState = useMemo(
    () =>
      buildQuestionBankFlowState({
        ...flowState,
        listQueryState: { currentPage, searchTerm, appliedFilters },
      }),
    [appliedFilters, currentPage, flowState, searchTerm],
  );
  useEffect(() => {
    persistQuestionBankFlowState(screenState);
  }, [screenState]);
  useEffect(() => {
    setCurrentPage(1);
    setLoadedPodcasts([]);
    loadedPages.current = new Set();
  }, [appliedFilters, debouncedSearchTerm, listScope]);
  useEffect(() => {
    if (isPlaceholderData || !data?.podcasts) return;
    const page = Number(data.pagination?.currentPage || currentPage);
    loadedPages.current.add(page);
    setLoadedPodcasts(current => {
      const items = new Map(current.map(item => [item.id, item]));
      data.podcasts.forEach(item => items.set(item.id, item));
      return [...items.values()];
    });
  }, [data, currentPage, isPlaceholderData]);
  useEffect(() => {
    const loadNextPage = () => {
      const next = currentPage + 1;
      if (pending || isError || next > Number(data?.pagination?.totalPages || 1) || loadedPages.current.has(next)) return;
      if (window.scrollY + window.innerHeight >= document.documentElement.scrollHeight - 240) setCurrentPage(next);
    };
    window.addEventListener('scroll', loadNextPage, { passive: true });
    return () => window.removeEventListener('scroll', loadNextPage);
  }, [currentPage, data?.pagination?.totalPages, pending, isError]);
  useEffect(() => {
    const debounceTimer = window.setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);

    return () => window.clearTimeout(debounceTimer);
  }, [searchTerm]);
  useEffect(() => {
    if (!isFilterOpen) return undefined;
    const onKeyDown = (event) =>
      event.key === "Escape" && setIsFilterOpen(false);
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKeyDown);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [isFilterOpen]);

  const stateName =
    data?.stateName ||
    resolveQuestionBankStateName({
      locationState: flowState,
      requestParams: queryParams,
      sidebarData,
    }) ||
    "State not selected";
  const title =
    data?.topicName ||
    data?.subjectName ||
    selectedTopic?.name ||
    flowState?.selectedSubject?.title ||
    "Podcasts";
  const errorMessage = isError
    ? error?.response?.data?.message ||
      error?.message ||
      "Unable to load podcasts."
    : "";
  const back = () =>
    flowState?.qbankBackTarget?.pathname
      ? navigate(flowState.qbankBackTarget.pathname, {
          replace: true,
          state: flowState.qbankBackTarget.state,
        })
      : navigate(EXPLORE_ROUTES.subjects, {
          replace: true,
          state: buildQuestionBankSubjectsState(flowState),
        });
  const openDetails = (podcast) => {
    const detailState = buildQuestionBankFlowState({
      ...podcastDetailFlow(podcast, { ...screenState, state_id: stateId, subject_id: subjectId }),
      qbankBackTarget: { pathname: CONTENT_ROUTES.podcasts, state: screenState },
    });
    persistQuestionBankFlowState(detailState);
    navigate(CONTENT_ROUTES.podcastDetail, { state: detailState });
  };
  const openPodcast = async podcast => {
    if (purchaseBusy.current) return;
    if (!podcastNeedsPurchase(podcast)) { openDetails(podcast); return; }
    purchaseBusy.current = true;
    try {
      await promptPodcastPurchase(podcast, { ...screenState, stateId, state_id: stateId, subjectId, subject_id: subjectId,
        subscriptionBackTarget: { pathname: CONTENT_ROUTES.podcasts, state: screenState } }, navigate, async () => {
        await Promise.all([refetch(), client.invalidateQueries({ queryKey: ['sidebarData'] })]);
        const { payload } = await fetchPodcastDetail({ ...queryParams, audio_podcast_id: podcast.id });
        const latest = payload.podcast_data;
        if (!latest?.audio_podcast_id || podcastNeedsPurchase(latest)) throw new Error('Podcast access has not been confirmed. Please try again.');
        openDetails({ ...podcast, raw: latest, freePaid: latest.free_paid, paymentStatus: Number(latest.payment_status) });
      });
    } finally { purchaseBusy.current = false; }
  };
  const totalPages = Number(data?.pagination?.totalPages || 1);

  return (
    <>
      <style>{`
    .homeDashboard-wrapper .page-content { padding-right: 0; }
    .homeDashboard-wrapper .main-content { background: linear-gradient(105deg, rgb(252, 242, 240) 0%, rgb(250, 238, 242) 25%, rgb(245, 235, 248) 50%, rgb(234, 238, 253) 75%, rgb(233, 242, 253) 100%); }
  `}</style>
      <HomeDashboardLayout showHeader={false}>
        <div className="qbl-page qbankList-page podcastListt">
          <div className="qbl-main" aria-label="Podcast content">
            <section className="qbl-panel">
              <div className="right-notify d-flex d-xl-none justify-content-between align-items-center">
                <button
                  type="button"
                  className="categories-back-button"
                  onClick={back}
                  aria-label="Go back"
                >
                  <i className="bi bi-chevron-left" />
                </button>
                <HeaderProfileSection />
              </div>
              <div className="qb-body">
                <div className="d-flex align-items-center justify-content-center">
                  <div className="qb-copy">
                    <div className="qb-loc">
                      <i className="bi bi-geo-alt-fill" />
                      <span>{stateName}</span>
                    </div>
                    <h1 className="qb-title">{title}</h1>
                    <p className="qb-sub qbb-list">
                      Learn faster with audio lessons.
                    </p>
                  </div>
                </div>
              </div>
              <div className="d-flex d-xl-block align-items-center mobile-filter-box">
                <label className="qbl-search">
                  <i className="bi bi-search" aria-hidden="true" />
                  <input
                    type="search"
                    placeholder="Search podcasts..."
                    value={searchTerm}
                    onChange={(event) => setSearchTerm(event.target.value)}
                    disabled={pending}
                    aria-label="Search podcasts"
                  />
                </label>
                <button
                  type="button"
                  className="mobile-Filter-icon d-flex d-lg-flex d-xl-none"
                  onClick={() => setIsFilterOpen(true)}
                  disabled={pending}
                  aria-label="Open podcast filters"
                  aria-expanded={isFilterOpen}
                  aria-controls="podcast-filter-drawer"
                >
                  <span className="filter-card__icon">
                    <i className="bi bi-sliders2" />
                  </span>
                </button>
              </div>
              <div className="qbl-summaryRow">
                <h2>
                  {pending
                    ? "Loading podcasts..."
                    : `${Number(data?.totalPodcasts || 0)} Podcasts Found`}
                </h2>
                <div className="d-flex align-items-center gap-2">
                  {Number(data?.advertEarnedCoins || 0) > 0 ? (
                    <span className="adCoins">
                      <span className="adCoinsIcon">
                        <i className="bi bi-play-fill" />
                      </span>
                      {`Ad Coins: ${data.advertEarnedCoins}`}
                    </span>
                  ) : null}
                  <button
                    type="button"
                    className="mobile-Filter-icon d-none d-xl-flex"
                    onClick={() => setIsFilterOpen(true)}
                    aria-label="Open podcast filters"
                  >
                    <span className="filter-card__icon">
                      <i className="bi bi-sliders2" />
                    </span>
                  </button>
                </div>
              </div>
              {errorMessage ? (
                <div className="choose-state__status choose-state__status--error">
                  {errorMessage}
                </div>
              ) : null}
              {!pending && !errorMessage && !loadedPodcasts.length ? (
                <div className="choose-state__status">
                  No podcasts available.
                </div>
              ) : null}
              {!errorMessage ? (
                <PodcastListCard
                  podcasts={loadedPodcasts}
                  isLoading={pending && !loadedPodcasts.length}
                  onOpen={openPodcast}
                  onBookmark={(podcast) =>
                    toggleBookmark.mutate({ audio_podcast_id: podcast.id }, { onSuccess: ({ payload }) =>
                      setLoadedPodcasts(items => items.map(item => item.id === podcast.id ? { ...item, isBookmarked: Number(payload.bookmark_status) === 1 } : item)) })
                  }
                  onLike={(podcast) =>
                    toggleLike.mutate({ audio_podcast_id: podcast.id }, { onSuccess: ({ payload }) =>
                      setLoadedPodcasts(items => items.map(item => item.id === podcast.id ? { ...item, isLiked: Number(payload.like_status) === 1, likeCount: Number(payload.total_liked || 0) } : item)) })
                  }
                  onView={setViewPodcast}
                />
              ) : null}
              {!pending && !errorMessage && totalPages > 1 ? (
                <div className="qbl-pagination">
                  <button
                    type="button"
                    className="qbl-pagination__nav"
                    disabled={currentPage <= 1}
                    onClick={() =>
                      setCurrentPage((page) => Math.max(1, page - 1))
                    }
                  >
                    Previous
                  </button>
                  <div className="qbl-pagination__pages">
                    {visiblePages(currentPage, totalPages).map((page) => (
                      <button
                        key={page}
                        type="button"
                        className={`qbl-pagination__page${page === currentPage ? " is-active" : ""}`}
                        onClick={() => setCurrentPage(page)}
                      >
                        {page}
                      </button>
                    ))}
                  </div>
                  <button
                    type="button"
                    className="qbl-pagination__nav"
                    disabled={currentPage >= totalPages}
                    onClick={() =>
                      setCurrentPage((page) => Math.min(totalPages, page + 1))
                    }
                  >
                    Next
                  </button>
                </div>
              ) : null}
              <div
                className={`qbl-filterBackdrop ${isFilterOpen ? "is-open" : ""}`}
                onClick={() => setIsFilterOpen(false)}
                aria-hidden="true"
              />
              <div
                id="podcast-filter-drawer"
                className={`qbl-filterPanel ${isFilterOpen ? "is-open" : ""}`}
              >
                <QBankFilter
                  className={isFilterOpen ? "is-open" : ""}
                  onClose={() => setIsFilterOpen(false)}
                  queryParams={filterQueryParams}
                  appliedFilters={appliedFilters}
                  onApply={(filters) => {
                    setAppliedFilters(filters || {});
                    setIsFilterOpen(false);
                  }}
                />
              </div>
              <PodcastViewModal
                open={Boolean(viewPodcast)}
                onClose={() => setViewPodcast(null)}
                podcast={viewPodcast}
              />
            </section>
          </div>
        </div>
      </HomeDashboardLayout>
    </>
  );
};

export default PodcastList;
